import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Check,
    Copy,
    Download,
    Eye,
    FileText,
    HelpCircle,
    Layers,
    Maximize2,
    Scissors,
    Sparkles,
    ThumbsUp,
    X,
} from "lucide-preact";
import type {
    ReactElement,
    RefObject,
} from "preact/compat";
import {
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    splClient,
} from "../../api/client.ts";
import {
    useAuthStore,
} from "../../stores/authStore.ts";
import {
    useViewerStore,
} from "../../stores/viewerStore.ts";
import {
    Tooltip,
} from "../common/Tooltip.tsx";
import {
    TagInput,
    TagPill,
} from "../tags/index.ts";
import {
    RecommendationsSidebar,
} from "./RecommendationsSidebar.tsx";

type ViewerRightSidebarProps = {
    open: boolean;
    tagInputRef: RefObject<HTMLInputElement | null>;
    onAddTag: (tag: string) => void;
    onRemoveTag: (tag: string) => void;
    onShowShortcuts: () => void;
    onToggleOcrOverlay: () => void;
    onToggleAiAnalysis: () => void;
    onToggleFullscreen: () => void;
    onAcceptSuggestion: (tag: string) => void;
    onRejectSuggestion: (tag: string) => void;
    onShowSimilar: () => void;
    onCrop: () => void;
    showOcrOverlay: boolean;
    voteRefreshKey: number;
    onNavigateToItem: (item: MediaItem) => void;
};

const getFileExtension = (url: string, mediaType: string): string => {
    try {
        const pathname = new URL(url).pathname;
        const lastDot = pathname.lastIndexOf(".");
        if (lastDot !== -1) {
            const ext = pathname.substring(lastDot + 1).toLowerCase();
            if (ext.length >= 2 && ext.length <= 5) return ext;
        }
    } catch { /* ignore */ }
    return mediaType === "video" ? "mp4" : "jpg";
};

export const ViewerRightSidebar = ({
    open,
    tagInputRef,
    onAddTag,
    onRemoveTag,
    onShowShortcuts,
    onToggleOcrOverlay,
    onToggleAiAnalysis,
    onToggleFullscreen,
    onAcceptSuggestion,
    onRejectSuggestion,
    onShowSimilar,
    onCrop,
    showOcrOverlay,
    voteRefreshKey,
    onNavigateToItem,
}: ViewerRightSidebarProps): ReactElement => {
    const {
        canTag,
        isAdmin,
    } = useAuthStore();

    const {
        currentItem,
    } = useViewerStore();

    // operators wanted suggestions to drop tags they've already accepted
    // (or that the item already had); checkbox lets the operator opt back
    // into the full list without persisting that preference across tabs.
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);

    // optimistic dismissal: when the operator clicks the X, the pill
    // disappears immediately rather than waiting for the server-side
    // mutation to round-trip. the actual dismiss is still sent (the
    // mutation strips the suggestion server-side); this set just keeps
    // the UI snappy. cleared per item so navigating to the next media
    // shows that item's full suggestion list again.
    const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(
        () => new Set(),
    );
    useEffect(() => {
        setLocallyDismissed(new Set());
    }, [currentItem?.id]);

    // the public viewer route at /p/:id requires a share token to render
    // (anything else 404s). a "permalink" therefore needs an actual share
    // row first; the link copied is the share's tokenized URL, not a bare
    // /p/:id. clicks create a new never-expiring share — the operator can
    // revoke from the Share modal if they want to manage them.
    const copyPermalink = async (): Promise<void> => {
        if (!currentItem) return;
        try {
            const share = await splClient.createShareLink(currentItem.id);
            await navigator.clipboard.writeText(share.shareUrl);
            toast.success("Permalink copied");
        } catch {
            toast.error("Failed to create permalink");
        }
    };

    return (
        <aside
            className={`
                fixed right-0 top-14 bottom-0 w-72 bg-bg-secondary border-l border-border
                transform transition-transform duration-300 z-20 overflow-y-auto
                ${open ? "translate-x-0" : "translate-x-full"}
            `}
        >
            <div className="p-4 space-y-5">
                {/* --- item info --- */}
                {currentItem && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
                            Item Info
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1.5 text-text-secondary">
                                <Eye size={14} />
                                <span>{currentItem.views} views</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-text-secondary">
                                <ThumbsUp size={14} />
                                <span className="text-accent font-medium">
                                    {currentItem.votes}
                                </span>{" "}
                                votes
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="px-2 py-0.5 bg-bg-tertiary rounded text-text-secondary uppercase">
                                {currentItem.media_type}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={copyPermalink}
                            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors group"
                        >
                            <Copy size={12} />
                            <span className="font-mono">
                                {currentItem.id.slice(0, 12)}...
                            </span>
                            <span className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
                                copy link
                            </span>
                        </button>
                    </div>
                )}

                {currentItem && <div className="border-t border-border" />}

                {/* --- actions --- */}
                {currentItem && (
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
                            Actions
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                            {currentItem.ocr_text && (
                                <Tooltip
                                    content="Show extracted text overlay. Shortcut: O"
                                    position="left"
                                    fullWidth
                                >
                                    <button
                                        type="button"
                                        onClick={onToggleOcrOverlay}
                                        className={`w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                                            showOcrOverlay
                                                ? "bg-accent text-white"
                                                : "bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border"
                                        }`}
                                    >
                                        <FileText size={14} />
                                        OCR (O)
                                    </button>
                                </Tooltip>
                            )}
                            {currentItem.ai_analyses &&
                                Object.keys(currentItem.ai_analyses).length >
                                    0 &&
                                (() => {
                                    const primary = getPrimaryAnalysis(
                                        currentItem.ai_analyses,
                                    );
                                    const confidence =
                                        primary?.entry.confidence ?? 0;
                                    const confidenceColor = confidence >= 0.8
                                        ? "bg-success"
                                        : confidence >= 0.5
                                        ? "bg-warning"
                                        : "bg-error";
                                    return (
                                        <Tooltip
                                            content={`AI analysis (${
                                                Math.round(confidence * 100)
                                            }% confidence). Shortcut: I`}
                                            position="left"
                                            fullWidth
                                        >
                                            <button
                                                type="button"
                                                onClick={onToggleAiAnalysis}
                                                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border transition-colors cursor-pointer"
                                            >
                                                <Sparkles size={14} />
                                                AI Info (I)
                                                <span
                                                    className={`ml-auto w-2 h-2 rounded-full ${confidenceColor}`}
                                                />
                                            </button>
                                        </Tooltip>
                                    );
                                })()}
                            <Tooltip
                                content="Enter fullscreen mode. Shortcut: F"
                                position="left"
                                fullWidth
                            >
                                <button
                                    type="button"
                                    onClick={onToggleFullscreen}
                                    className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border transition-colors cursor-pointer"
                                >
                                    <Maximize2 size={14} />
                                    Fullscreen (F)
                                </button>
                            </Tooltip>
                            <Tooltip
                                content="Download original file"
                                position="left"
                                fullWidth
                            >
                                <a
                                    href={currentItem.media_url}
                                    download={`spl-${currentItem.id}.${
                                        getFileExtension(
                                            currentItem.media_url,
                                            currentItem.media_type,
                                        )
                                    }`}
                                    className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border transition-colors cursor-pointer"
                                >
                                    <Download size={14} />
                                    Download
                                </a>
                            </Tooltip>
                            {currentItem.ai_analyses &&
                                Object.keys(currentItem.ai_analyses).length >
                                    0 &&
                                (
                                    <Tooltip
                                        content="Browse similar items"
                                        position="left"
                                        fullWidth
                                    >
                                        <button
                                            type="button"
                                            onClick={onShowSimilar}
                                            className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border transition-colors cursor-pointer"
                                        >
                                            <Layers size={14} />
                                            Similar
                                        </button>
                                    </Tooltip>
                                )}
                            {isAdmin && currentItem.media_type === "image" && (
                                <Tooltip
                                    content="Crop image. Shortcut: C"
                                    position="left"
                                    fullWidth
                                >
                                    <button
                                        type="button"
                                        onClick={onCrop}
                                        className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border transition-colors cursor-pointer"
                                    >
                                        <Scissors size={14} />
                                        Crop (C)
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                )}

                {currentItem && <div className="border-t border-border" />}

                {/* --- AI suggested tags --- */}
                {(() => {
                    const primaryAnalysis = getPrimaryAnalysis(
                        currentItem?.ai_analyses,
                    );
                    const allSuggested = primaryAnalysis?.entry.suggested_tags;
                    if (
                        !allSuggested || allSuggested.length === 0 ||
                        !canTag()
                    ) return null;

                    const appliedTagSet = new Set(currentItem?.tags ?? []);
                    const visibleSuggestions = showAllSuggestions
                        ? allSuggested
                        : allSuggested.filter((tag) =>
                            !appliedTagSet.has(tag) &&
                            !locallyDismissed.has(tag)
                        );
                    const hiddenCount = allSuggested.length -
                        visibleSuggestions.length;

                    if (
                        visibleSuggestions.length === 0 && !showAllSuggestions
                    ) {
                        return null;
                    }

                    return (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <label className="text-sm text-text-secondary flex items-center gap-1.5">
                                    <Sparkles
                                        size={14}
                                        className="text-accent"
                                    />
                                    AI Suggestions
                                </label>
                                <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={showAllSuggestions}
                                        onChange={() =>
                                            setShowAllSuggestions((prev) =>
                                                !prev
                                            )}
                                        className="rounded border-border"
                                    />
                                    Show all
                                    {hiddenCount > 0 && !showAllSuggestions && (
                                        <span className="text-text-muted">
                                            (+{hiddenCount})
                                        </span>
                                    )}
                                </label>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {visibleSuggestions.map((tag: string) => (
                                    <div
                                        key={tag}
                                        className="flex items-center gap-0.5 pl-2 pr-0.5 py-1 bg-accent/10 border border-accent/20 rounded-full text-xs"
                                    >
                                        <span className="text-accent">
                                            {tag}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLocallyDismissed((prev) => {
                                                    const next = new Set(prev);
                                                    next.add(tag);
                                                    return next;
                                                });
                                                onAcceptSuggestion(tag);
                                            }}
                                            className="p-0.5 rounded-full hover:bg-success/20 text-text-secondary hover:text-success transition-colors cursor-pointer"
                                            title="Accept suggestion"
                                        >
                                            <Check size={12} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLocallyDismissed((prev) => {
                                                    const next = new Set(prev);
                                                    next.add(tag);
                                                    return next;
                                                });
                                                onRejectSuggestion(tag);
                                            }}
                                            className="p-0.5 rounded-full hover:bg-error/20 text-text-secondary hover:text-error transition-colors cursor-pointer"
                                            title="Dismiss suggestion"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* --- add custom tag --- */}
                {canTag() && (
                    <div className="space-y-2">
                        <label className="text-sm text-text-secondary">
                            Add Tag (T)
                        </label>
                        <TagInput
                            inputRef={tagInputRef}
                            onAddTag={onAddTag}
                            existingTags={currentItem?.tags || []}
                        />
                    </div>
                )}

                {/* --- current tags --- */}
                {currentItem && currentItem.tags.length > 0 && (
                    <div className="space-y-2">
                        <label className="text-sm text-text-secondary">
                            Current Tags
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {currentItem.tags.map((tag) => (
                                <TagPill
                                    key={tag}
                                    tag={tag}
                                    showRemove={canTag()}
                                    onRemove={canTag()
                                        ? () => onRemoveTag(tag)
                                        : undefined}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className="border-t border-border" />

                {/* --- shortcuts link --- */}
                <button
                    type="button"
                    onClick={onShowShortcuts}
                    className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                    <HelpCircle size={16} />
                    Keyboard Shortcuts (?)
                </button>

                <RecommendationsSidebar
                    refreshKey={voteRefreshKey}
                    onNavigateToItem={onNavigateToItem}
                />
            </div>
        </aside>
    );
};
